import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    Database,
    Eye,
    EyeOff,
    Files,
    Network,
    Factory,
    ShieldCheck,
    Search,
    UsersRound,
    Wrench,
    Images
} from "lucide-react";

import { hasRole, loginAdmin, subscribeToAuth } from "../api/auth";
import ComponentRowsSearchModal from "../modals/ComponentRowsSearchModal";
import SiteParsersPage from "./SiteParsersPage";
import Bm25Page from "./Bm25Page";
import AdminDataQualityPanel from "./AdminDataQualityPanel";
import AdminCollectionControlPanel from "./AdminCollectionControlPanel";
import AdminMaintenancePage from "./AdminMaintenancePage";
import CharacteristicDictionaryPage from "./CharacteristicDictionaryPage";
import ManufacturerMappingPage from "./ManufacturerMappingPage";
import CatalogTaxonomyPage from "./CatalogTaxonomyPage";
import CatalogCategoryPhotosPage from "./CatalogCategoryPhotosPage";
import DocumentArchivePage from "./DocumentArchivePage";
import UsersPage from "./UsersPage";
import styles from "./AdminPage.module.css";

const TABS = {
    componentSearch: { label: "Поиск номенклатуры", subtitle: "История запросов и предложений, цены и динамика по артикулу.", icon: Search },
    parsers: { label: "Сайты и парсеры", subtitle: "Источники данных, правила сбора и включение для всего приложения.", icon: Database },
    users: {
        label: "Пользователи",
        subtitle: "Сотрудники, МЗ/МП и настройки доступа к рабочим документам.",
        icon: UsersRound
    },
    documents: {
        label: "Формирование документов",
        subtitle: "Шаблоны, теги и генерация архива документов.",
        icon: Files
    },
    superset: {
        label: "Загрузка в Superset",
        subtitle: "Загрузка базы, просмотр периодов и удаление месячных срезов.",
        icon: Database
    },
    maintenance: {
        label: "Обслуживание",
        subtitle: "Служебные операции для проверки и обновления данных.",
        icon: Wrench
    },
    dataQuality: { label: "Качество данных", subtitle: "Полнота карточек, неудачные попытки и расхождения источников.", icon: ShieldCheck },
    characteristics: { label: "Характеристики", subtitle: "Справочник RU/EN, канонические значения и очередь предложений LLM.", icon: Database },
    manufacturers: { label: "Производители", subtitle: "DigiKey, автоматические и ручные соответствия производителей.", icon: Factory },
    catalogTaxonomy: {
        label: "Категории каталога",
        subtitle: "Единая иерархия и сопоставление категорий поставщиков вручную или через DeepSeek.",
        icon: Network
    },
    categoryPhotos: { label: "Фото категорий", subtitle: "Просмотр и замена фотографий в каталоге товаров.", icon: Images },
    collectionControl: { label: "Расписания и задания", subtitle: "Настройка расписаний, журнал запусков и выполнение регламентных заданий.", icon: Database }
};

export default function AdminPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS[searchParams.get("tab")] ? searchParams.get("tab") : "users";
    const [activeTab, setActiveTab] = useState(initialTab);
    const [authenticated, setAuthenticated] = useState(hasRole("ADMIN"));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    useEffect(() => {
        return subscribeToAuth(() => setAuthenticated(hasRole("ADMIN")));
    }, []);

    const selectTab = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        if (!email.trim() || !password) {
            setError("Введите email и пароль");
            return;
        }

        setLoading(true);
        setError("");

        try {
            await loginAdmin(email, password);
            setEmail("");
            setPassword("");
            setAuthenticated(true);
        } catch (loginError) {
            setError(loginError.message || "Не удалось войти");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className={styles.page}>
            <div className={styles.shell}>
                <header className={styles.header}>
                    <span className={styles.headerIcon} aria-hidden="true">
                        <ShieldCheck size={25} />
                    </span>
                    <div className={styles.titleBlock}>
                        <span className={styles.eyebrow}>Системное управление</span>
                        <h1 className={styles.title}>Администрирование</h1>
                        <p className={styles.subtitle}>
                            {authenticated ? TABS[activeTab].subtitle : "Вход сотрудника с ролью ADMIN."}
                        </p>
                    </div>

                    <div className={styles.headerActions}>
                        <button className={styles.linkButton} type="button" onClick={() => navigate("/?tab=catalog")}>
                            <ArrowLeft size={16} />
                            К каталогу
                        </button>
                    </div>
                </header>

                {!authenticated ? (
                    <div className={styles.loginGridSingle}>
                        <form className={styles.loginPanel} onSubmit={handleLogin}>
                            <label className={styles.fieldLabel} htmlFor="admin-email">
                                Email
                            </label>
                            <input
                                id="admin-email"
                                className={styles.passwordInput}
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                placeholder="admin@example.com"
                            />

                            <label className={styles.fieldLabel} htmlFor="admin-password">
                                Пароль
                            </label>
                            <div className={styles.passwordField}>
                                <input
                                    id="admin-password"
                                    className={styles.passwordInput}
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={event => setPassword(event.target.value)}
                                />
                                <button
                                    className={styles.passwordToggle}
                                    type="button"
                                    onClick={() => setShowPassword(value => !value)}
                                    aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                                    title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>

                            {error && <p className={styles.errorText}>{error}</p>}

                            <div className={styles.loginActions}>
                                <button className={styles.loginButton} type="submit" disabled={loading}>
                                    {loading ? "Вход..." : "Войти"}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <>
                        <nav className={styles.tabs} aria-label="Раздел администрирования">
                            {Object.entries(TABS).map(([key, tab]) => {
                                const Icon = tab.icon;
                                return (
                                <button
                                    key={key}
                                    className={`${styles.tabButton} ${activeTab === key ? styles.tabButtonActive : ""}`}
                                    type="button"
                                    aria-current={activeTab === key ? "page" : undefined}
                                    onClick={() => selectTab(key)}
                                >
                                    <Icon size={16} />
                                    {tab.label}
                                </button>
                                );
                            })}
                        </nav>

                        <section className={styles.panel}>
                            {activeTab === "componentSearch" && <ComponentRowsSearchModal embedded />}
                            {activeTab === "users" && <UsersPage embedded />}
                            {activeTab === "documents" && <DocumentArchivePage embedded />}
                            {activeTab === "superset" && <Bm25Page embedded />}
                            {activeTab === "dataQuality" && <AdminDataQualityPanel />}
                            {activeTab === "collectionControl" && <AdminCollectionControlPanel />}
                            {activeTab === "maintenance" && <AdminMaintenancePage />}
                            {activeTab === "parsers" && <SiteParsersPage />}
                            {activeTab === "characteristics" && <CharacteristicDictionaryPage />}
                            {activeTab === "manufacturers" && <ManufacturerMappingPage />}
                            {activeTab === "catalogTaxonomy" && <CatalogTaxonomyPage />}
                            {activeTab === "categoryPhotos" && <CatalogCategoryPhotosPage />}
                        </section>
                    </>
                )}
            </div>
        </main>
    );
}
